import { useEffect, useState } from "react";
import intl from "react-intl-universal";
import { Link, Redirect, useParams } from "react-router-dom";
import { TbArrowLeft, TbBuildingBank } from "react-icons/tb";
import { getCardNetworkLabel } from "../../../../Components/Atoms/CardNetworkLogo";
import { useBaasInstance } from "../../../../Contexts/BaasInstanceContext";
import { useToast } from "../../../../Hooks/toast";
import { useBaasCapabilities } from "../../../../Hooks/useBaasCapabilities";
import { useBaasPageLoading } from "../../../../Hooks/useBaasPageLoading";
import { BaasCardProgramDetailSkeleton } from "../components/BaasSkeleton";
import {
  fetchBaasCardProgramById,
  updateBaasCardProgramDesign,
  updateBaasCardProgramPlannedCredit,
} from "../../../../Services/baasCardProgramsApi";
import { formatBaasConsoleConflictMessage } from "../../../../Services/baasConsoleClient";
import { formatInputValue } from "../../../../Utils/formatInputValue";
import { formatMonetaryValue } from "../../../../Utils/formatters";
import {
  formatBrlMajorMask,
  parseBrlMajorMask,
} from "../Instances/baasInstanceCreateConstants";
import { DEFAULT_PRODUCT_DESIGN, isCobrandedProgramKind } from "./cardProgramDesignConstants";
import { BaasCardProgramDesignForm } from "./components/BaasCardProgramDesignForm";
import { BaasCartoesKpiCards } from "./components/BaasCartoesKpiCards";
import { BaasCreditoKpiCards } from "./components/BaasCreditoKpiCards";
import { BaasFundShellsButton } from "../Accounts/components/BaasFundShellsButton";
import {
  BAAS_ACCOUNTS_PATH,
} from "../constants";

const DETAIL_TABS = [
  "issuances",
  "guarantee",
  "productDesign",
  "authorizer",
  "settlement",
];

const formatCount = (value) =>
  new Intl.NumberFormat("pt-BR", { maximumFractionDigits: 0 }).format(value);

/** Major-unit amounts (planned credit / reserve) — pt-BR R$ display. */
const formatMoneyMajor = (amount, currency = "BRL") =>
  formatMonetaryValue(amount, currency || "BRL");

const DetailRow = ({ label, value }) => (
  <div className="flex flex-col gap-0.5 border-b border-gray-100 py-3 last:border-0 dark:border-brand-dark-border sm:flex-row sm:items-center sm:justify-between">
    <span className="text-sm text-gray-500 dark:text-gray-400">{label}</span>
    <span className="text-sm font-medium text-brand-dark dark:text-gray-100">{value}</span>
  </div>
);

const inputClass =
  "w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-brand-dark outline-none focus:border-gray-400 dark:border-brand-dark-border dark:bg-brand-dark-elevated dark:text-gray-100";

const GuaranteePanel = ({ program, instance, onSaved }) => {
  const { addToast } = useToast();
  const { can } = useBaasCapabilities();
  const canWrite = can("issuer.programs.create");
  const currency = program.guaranteeCurrency || "BRL";
  const reserve = program.reserve;
  const [plannedCredit, setPlannedCredit] = useState(() =>
    program.plannedCreditAmount != null && program.plannedCreditAmount !== ""
      ? formatBrlMajorMask(program.plannedCreditAmount)
      : "",
  );
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setPlannedCredit(
      program.plannedCreditAmount != null && program.plannedCreditAmount !== ""
        ? formatBrlMajorMask(program.plannedCreditAmount)
        : "",
    );
  }, [program.id, program.plannedCreditAmount]);

  const plannedNumber = parseBrlMajorMask(plannedCredit);
  const livePlanned = Number.isFinite(plannedNumber) && plannedNumber >= 0 ? plannedNumber : 0;
  const liveRequired = Number(
    (livePlanned * ((Number(program.guaranteePercent) || 0) / 100)).toFixed(2),
  );
  const reserveAvailable = Number(reserve?.available ?? 0) || 0;
  const reserveShort = liveRequired > reserveAvailable;

  const handleSave = async () => {
    if (!instance || !canWrite) return;
    if (!Number.isFinite(plannedNumber) || plannedNumber < 0) {
      addToast({
        type: "error",
        title: intl.get("baas.cartoes.programs.detail.plannedCreditInvalid"),
      });
      return;
    }

    setSaving(true);
    try {
      const updated = await updateBaasCardProgramPlannedCredit(
        instance,
        program.id,
        plannedNumber,
      );
      if (updated) {
        onSaved(updated);
        addToast({
          type: "success",
          title: intl.get("baas.cartoes.programs.detail.plannedCreditSaveSuccess"),
        });
      }
    } catch (error) {
      addToast({
        type: "error",
        title: formatBaasConsoleConflictMessage(
          error,
          intl.get("baas.cartoes.programs.detail.plannedCreditSaveError"),
        ),
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4 rounded-xl border border-gray-200 bg-white p-4 dark:border-brand-dark-border dark:bg-brand-dark-surface">
      {canWrite ? (
        <label className="block space-y-1.5">
          <span className="text-xs font-semibold text-gray-600 dark:text-gray-300">
            {intl.get("baas.cartoes.programs.detail.plannedCreditAmount")}
          </span>
          <div className="relative">
            <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">
              R$
            </span>
            <input
              className={`${inputClass} pl-9`}
              type="text"
              inputMode="numeric"
              value={plannedCredit}
              onChange={(e) => setPlannedCredit(formatInputValue(String(e.target.value ?? "")))}
              disabled={saving}
              aria-label={intl.get("baas.cartoes.programs.detail.plannedCreditAmount")}
            />
          </div>
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {intl.get("baas.cartoes.programs.detail.plannedCreditHint")}
          </span>
        </label>
      ) : (
        <DetailRow
          label={intl.get("baas.cartoes.programs.detail.plannedCreditAmount")}
          value={formatMoneyMajor(program.plannedCreditAmount, currency)}
        />
      )}
      <DetailRow
        label={intl.get("baas.cartoes.programs.detail.guaranteeAmount")}
        value={formatMoneyMajor(liveRequired, currency)}
      />
      <DetailRow
        label={intl.get("baas.cartoes.programs.detail.guaranteePercent")}
        value={`${program.guaranteePercent}%`}
      />
      {program.programReserveAccountId ? (
        <DetailRow
          label={intl.get("baas.accounts.accountTypeReserve")}
          value={program.programReserveAccountId}
        />
      ) : null}
      {reserve ? (
        <>
          <DetailRow
            label={intl.get("baas.cartoes.programs.detail.reserveAvailable")}
            value={formatMoneyMajor(reserve.available, reserve.currency)}
          />
          <DetailRow
            label={intl.get("baas.cartoes.programs.detail.reserveHeld")}
            value={formatMoneyMajor(reserve.held, reserve.currency)}
          />
          <DetailRow
            label={intl.get("baas.cartoes.programs.detail.reserveBalance")}
            value={formatMoneyMajor(reserve.balance, reserve.currency)}
          />
        </>
      ) : null}
      {reserveShort ? (
        <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-300">
          {intl.get("baas.cartoes.programs.detail.reserveShortfall", {
            required: formatMoneyMajor(liveRequired, currency),
            available: formatMoneyMajor(reserveAvailable, currency),
          })}
        </p>
      ) : null}
      <div className="flex flex-wrap justify-end gap-2 border-t border-gray-200 pt-4 dark:border-brand-dark-border">
        <BaasFundShellsButton
          destinationAccountId={program.programReserveAccountId}
          destinationRole="program_reserve"
          className="btn-outline !text-xs"
        />
        {canWrite ? (
          <button
            type="button"
            className="btn-primary !text-xs"
            onClick={handleSave}
            disabled={saving}
          >
            {intl.get("baas.cartoes.programs.detail.plannedCreditSave")}
          </button>
        ) : null}
      </div>
    </div>
  );
};

const ProductDesignPanel = ({ program, instance, onSaved }) => {
  const { addToast } = useToast();
  const [design, setDesign] = useState({
    ...DEFAULT_PRODUCT_DESIGN,
    ...program.productDesign,
    name: program.productDesign?.name ?? program.name,
  });
  const [saving, setSaving] = useState(false);
  const nameAndLogoLocked =
    String(program.status || program.productDesign?.status || "")
      .trim()
      .toUpperCase() === "ACTIVE";

  const handleSave = async () => {
    if (!nameAndLogoLocked && !design.name?.trim()) {
      addToast({
        type: "error",
        title: intl.get("baas.cartoes.create.validation.programNameRequired"),
      });
      return;
    }

    if (!instance) return;

    setSaving(true);
    try {
      const updated = await updateBaasCardProgramDesign(
        instance,
        program.id,
        {
          ...design,
          name: design.name?.trim() || design.name,
        },
        { omitNameAndLogo: nameAndLogoLocked },
      );
      if (updated) {
        onSaved(updated);
        addToast({
          type: "success",
          title: intl.get("baas.cartoes.design.saveSuccess"),
        });
      }
    } catch (error) {
      addToast({
        type: "error",
        title: formatBaasConsoleConflictMessage(
          error,
          intl.get("baas.cartoes.create.validation.programNameRequired"),
        ),
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <BaasCardProgramDesignForm
        design={design}
        onChange={setDesign}
        nameAndLogoLocked={nameAndLogoLocked}
      />
      <div className="flex justify-end border-t border-gray-200 pt-4 dark:border-brand-dark-border">
        <button
          type="button"
          className="btn-primary !text-xs disabled:cursor-not-allowed disabled:opacity-60"
          onClick={handleSave}
          disabled={saving || nameAndLogoLocked}
        >
          {intl.get("baas.cartoes.design.save")}
        </button>
      </div>
    </div>
  );
};

const TabPanel = ({ program, tab, instance, onProgramUpdate }) => {
  const authorizerStatus = program.authorizerConfig?.status ?? "pending";

  switch (tab) {
    case "issuances":
      return (
        <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-brand-dark-border dark:bg-brand-dark-surface">
          <DetailRow
            label={intl.get("baas.cartoes.programs.detail.totalIssuances")}
            value={formatCount(program.issuancesCount)}
          />
          <DetailRow
            label={intl.get("baas.cartoes.programs.detail.programName")}
            value={program.name}
          />
        </div>
      );
    case "guarantee":
      return (
        <GuaranteePanel
          key={program.id}
          program={program}
          instance={instance}
          onSaved={onProgramUpdate}
        />
      );
    case "productDesign":
      return (
        <ProductDesignPanel
          key={program.id}
          program={program}
          instance={instance}
          onSaved={onProgramUpdate}
        />
      );
    case "authorizer":
      return (
        <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-brand-dark-border dark:bg-brand-dark-surface">
          <DetailRow
            label={intl.get("baas.cartoes.create.config.fundingMode")}
            value={intl.get(
              program.fundingMode === "linked_account"
                ? "baas.cartoes.create.config.fundingModeLinkedAccount"
                : "baas.cartoes.create.config.fundingModePixKey",
            )}
          />
          {program.fundingMode === "linked_account" ? (
            <DetailRow
              label={intl.get("baas.cartoes.create.config.fundingAccount")}
              value={program.fundingAccountId || "—"}
            />
          ) : null}
          <DetailRow
            label={intl.get("baas.cartoes.create.config.programKind")}
            value={intl.get(
              isCobrandedProgramKind(program.programKind)
                ? "baas.cartoes.create.config.programKindCobranded"
                : "baas.cartoes.create.config.programKindNative",
            )}
          />
          <DetailRow
            label={intl.get("baas.cartoes.create.config.processor")}
            value={intl.get("baas.cartoes.create.config.processorRouteSummary", {
              brand:
                program.processor?.brandName
                || program.authorizerConfig?.provider
                || "Valut Matrix",
              route: program.processor?.routeLabel || "Roteamento Brasil",
            })}
          />
          <DetailRow
            label={intl.get("baas.cartoes.programs.detail.authorizerProvider")}
            value={program.authorizerConfig?.provider}
          />
          {isCobrandedProgramKind(program.programKind) ? (
            <DetailRow
              label={intl.get("baas.cartoes.create.config.authorizerClientUrl")}
              value={program.authorizerConfig?.clientUrl || "—"}
            />
          ) : null}
          <DetailRow
            label={intl.get("baas.cartoes.programs.detail.authorizerStatus")}
            value={intl.get(`baas.cartoes.programs.authorizerStatus.${authorizerStatus}`)}
          />
          <DetailRow
            label={intl.get("baas.settings.webhookUrl")}
            value={program.config?.webhookUrl || "—"}
          />
        </div>
      );
    case "settlement":
      return (
        <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-brand-dark-border dark:bg-brand-dark-surface">
          <DetailRow
            label={intl.get("baas.cartoes.programs.detail.settlementType")}
            value={program.settlementSchedule?.type || "—"}
          />
          <DetailRow
            label={intl.get("baas.cartoes.programs.detail.settlementFrequency")}
            value={intl.get(
              `baas.cartoes.programs.settlement.frequency.${program.settlementSchedule?.frequency || "daily"}`,
            )}
          />
          {(program.settlementSchedule?.invoiceDays?.length ?? 0) > 0 ? (
            <DetailRow
              label={intl.get("baas.cartoes.create.config.invoiceDays")}
              value={program.settlementSchedule.invoiceDays
                .map((day) => String(day).padStart(2, "0"))
                .join(", ")}
            />
          ) : null}
          {program.settlementSchedule?.cutoffTime ? (
            <DetailRow
              label={intl.get("baas.cartoes.programs.detail.cutoffTime")}
              value={program.settlementSchedule.cutoffTime}
            />
          ) : null}
        </div>
      );
    default:
      return null;
  }
};

export const BaasCardProgramDetailPage = () => {
  const { programId } = useParams();
  const { selectedInstance } = useBaasInstance();
  const { addToast } = useToast();
  const { can } = useBaasCapabilities();
  const pageLoading = useBaasPageLoading(
    selectedInstance?.id ? `${selectedInstance.id}:${programId}` : programId,
  );
  const [program, setProgram] = useState(null);
  const [dataLoading, setDataLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [activeTab, setActiveTab] = useState("productDesign");

  useEffect(() => {
    let cancelled = false;

    (async () => {
      if (!selectedInstance || !programId) {
        setProgram(null);
        setNotFound(true);
        setDataLoading(false);
        return;
      }

      setDataLoading(true);
      setNotFound(false);
      try {
        const next = await fetchBaasCardProgramById(selectedInstance, programId);
        if (!cancelled) {
          setProgram(next);
          setNotFound(!next);
        }
      } catch (error) {
        if (!cancelled) {
          setProgram(null);
          setNotFound(true);
          if (error?.status !== 404) {
            addToast({
              type: "error",
              title: formatBaasConsoleConflictMessage(
                error,
                intl.get("baas.cartoes.programs.empty"),
              ),
            });
          }
        }
      } finally {
        if (!cancelled) setDataLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [addToast, programId, selectedInstance]);

  if (pageLoading || dataLoading) {
    return <BaasCardProgramDetailSkeleton />;
  }

  if (notFound || !program) {
    return <Redirect to="/baas/cartoes" />;
  }

  const designStatus = program.productDesign?.status ?? "draft";
  const canViewAccounts = can("accounts.view");
  // Contas (shells-only for emissor) — open a shell card to see aporte extrato.
  const contasPath = BAAS_ACCOUNTS_PATH;

  return (
    <div className="baas-processing min-w-0 space-y-5">
      <header className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0">
          <p className="text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
            {intl.get("baas.cartoes.programs.detail.eyebrow")}
          </p>
          <div className="mt-1 flex flex-wrap items-center gap-3">
            <h1 className="text-2xl font-bold text-brand-dark dark:text-gray-100">{program.name}</h1>
            <span className="inline-flex items-center rounded-md bg-gray-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-600 dark:bg-brand-dark-elevated dark:text-gray-400">
              {getCardNetworkLabel(program.productDesign?.network)}
            </span>
            <span className="inline-flex items-center rounded-md bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400">
              {intl.get(`baas.cartoes.programs.designStatus.${designStatus}`)}
            </span>
          </div>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {intl.get("baas.cartoes.programs.detail.subtitle")}
          </p>
        </div>

        <div className="flex shrink-0 flex-wrap gap-2 self-start">
          {canViewAccounts ? (
            <Link to={contasPath} className="btn-outline w-auto !text-xs">
              <TbBuildingBank className="h-4 w-4" />
              {intl.get("baas.cartoes.programs.detail.accountsNav")}
            </Link>
          ) : null}
          <Link to="/baas/cartoes" className="btn-outline w-auto !text-xs">
            <TbArrowLeft className="h-4 w-4" />
            {intl.get("baas.cartoes.programs.detail.back")}
          </Link>
        </div>
      </header>

      <BaasCreditoKpiCards
        titleKey="baas.cartoes.programs.detail.creditSection"
        metrics={
          program.creditSummary || {
            currency: program.guaranteeCurrency || "BRL",
            totalLimitBrl: 0,
            runningCreditBrl: 0,
            utilizationRate: 0,
            unsecuredRate: 0,
            collateralizedRate: 0,
            netExposureBrl: 0,
          }
        }
      />
      <BaasCartoesKpiCards
        titleKey="baas.cartoes.programs.detail.totalsSection"
        metrics={
          program.cardTotals || {
            totalCards: program.issuancesCount || 0,
            activeCards: 0,
            activationRate: 0,
            approvalRate: 0,
            chargebackRatio: 0,
          }
        }
      />

      <div className="flex flex-wrap gap-2 border-b border-gray-200 dark:border-brand-dark-border">
        {DETAIL_TABS.map((tab) => (
          <button
            key={tab}
            type="button"
            onClick={() => setActiveTab(tab)}
            className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium transition ${
              activeTab === tab
                ? "border-brand-dark text-brand-dark dark:border-gray-100 dark:text-gray-100"
                : "border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            }`}
          >
            {intl.get(`baas.cartoes.programs.tabs.${tab}`)}
          </button>
        ))}
      </div>

      <TabPanel
        program={program}
        tab={activeTab}
        instance={selectedInstance}
        onProgramUpdate={setProgram}
      />
    </div>
  );
};
